(() => {
  // ---------- helpers ----------
  function getProductFromPicker(picker) {
    const jsonEl = picker.querySelector('script[type="application/json"]');
    if (!jsonEl) return null;
    try {
      return JSON.parse(jsonEl.textContent).product || null;
    } catch (e) {
      return null;
    }
  }

  function getSelectedOptions(picker) {
    const selected = [];
    picker.querySelectorAll('input.js-option:checked').forEach((input) => {
      const label = picker.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      if (!label) return;
      const idx = parseInt(label.getAttribute('data-option-index'), 10);
      if (!Number.isFinite(idx)) return;
      selected[idx] = input.value;
    });
    return selected;
  }

  function allOptionsChosen(product, selectedOptions) {
    const optionCount = product?.options?.length || 0;
    if (!optionCount) return false;
    for (let i = 0; i < optionCount; i++) {
      if (!selectedOptions[i]) return false;
    }
    return true;
  }

  function findExactVariant(product, selectedOptions) {
    if (!product?.variants?.length) return null;
    return (
      product.variants.find((v) => {
        if (!Array.isArray(v.options)) return false;
        for (let i = 0; i < product.options.length; i++) {
          if (v.options[i] !== selectedOptions[i]) return false;
        }
        return true;
      }) || null
    );
  }

  function updateSwatchClasses(picker, product, selected) {
    const variants = product.variants;

    picker.querySelectorAll('label[data-option-index][data-option-value]').forEach((label) => {
      const optIndex = parseInt(label.getAttribute('data-option-index'), 10);
      const optValue = label.getAttribute('data-option-value');
      if (!Number.isFinite(optIndex) || !optValue) return;

      const isAvailable = variants.some((v) => {
        if (!v.available) return false;
        if (!v.options || v.options[optIndex] !== optValue) return false;

        for (let i = 0; i < selected.length; i++) {
          if (i === optIndex) continue;
          if (selected[i] && v.options[i] !== selected[i]) return false;
        }
        return true;
      });

      label.classList.toggle('in-stock', isAvailable);
      label.classList.toggle('out-of-stock', !isAvailable);
    });
  }

  // Make the theme react like the normal variant-picker would.
  function dispatchThemeVariantChange(picker, variantOrNull) {
    picker.dispatchEvent(
      new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: { variant: variantOrNull }
      })
    );

    // Fallback: ensure the hidden "id" input is correct
    const form = picker.closest('form') || picker.closest('product-form')?.querySelector('form');
    if (form) {
      const idInput = form.querySelector('input[name="id"]');
      if (idInput && variantOrNull?.id) idInput.value = String(variantOrNull.id);
    }
  }

  // Klaviyo BIS container (class-based; safe for PDP + QuickAdd)
  function updateKlaviyoContainer(picker, product, variantOrNull) {
    const form = picker.closest('form') || picker.closest('product-form')?.querySelector('form');
    if (!form) return;

    // IMPORTANT: class selector (NOT id)
    const container = form.querySelector('.klaviyo-bis-button-container');
    if (!container) return;

    const shouldShow = !!(variantOrNull && variantOrNull.available === false);

    // Always keep attrs up to date
    container.dataset.productId = String(product.id);
    container.dataset.variantId = variantOrNull ? String(variantOrNull.id) : '';

    if (!shouldShow) {
      container.style.display = 'none';
      return;
    }

    // Show it
    container.style.display = '';

    /**
     * Force Klaviyo to render:
     * Many installs only render on initial scan / node insert, not attribute change.
     * Most reliable approach: replace the node entirely.
     */
    const replacement = container.cloneNode(false); // empty clone
    replacement.style.display = '';
    replacement.dataset.productId = container.dataset.productId;
    replacement.dataset.variantId = container.dataset.variantId;

    container.replaceWith(replacement);

    /**
     * Optional: if Klaviyo is loaded and reacts to events, this can help.
     * Safe no-op if _learnq is absent.
     */
    try {
      if (window._learnq && typeof window._learnq.push === 'function') {
        window._learnq.push(['track', 'Viewed Product', { productId: product.id, variantId: replacement.dataset.variantId }]);
      }
    } catch (e) {}
  }

  function updateAll(picker) {
    const product = getProductFromPicker(picker);
    if (!product || !Array.isArray(product.variants)) return;

    const selected = getSelectedOptions(picker);

    updateSwatchClasses(picker, product, selected);

    const complete = allOptionsChosen(product, selected);
    const exactVariant = complete ? findExactVariant(product, selected) : null;

    // If options are complete but no exact variant exists => "Unavailable combo"
    dispatchThemeVariantChange(picker, exactVariant);

    // Update Klaviyo container (uses exactVariant availability)
    updateKlaviyoContainer(picker, product, exactVariant);
  }

  function bindPicker(picker) {
    if (picker.dataset.bisBound === 'true') return;
    picker.dataset.bisBound = 'true';

    // Initial run
    updateAll(picker);

    // React to option changes
    picker.addEventListener('change', (e) => {
      if (e.target.matches('input.js-option')) updateAll(picker);
    });
  }

  function init(root = document) {
    root.querySelectorAll('variant-picker').forEach(bindPicker);
  }

  // Initial boot
  init(document);

  // Re-init hook for injected content (quick add drawer)
  document.addEventListener('variant-picker-bis:reinit', (e) => {
    init(e.detail?.root || document);
  });
})();
